import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import {
  CompoundEntityRef,
  Entity,
  getCompoundEntityRef,
  parseEntityRef,
  stringifyEntityRef,
} from '@backstage/catalog-model';
import { CheckResult } from '@backstage-community/plugin-tech-insights-common';
import { techInsightsApiRef } from '@backstage-community/plugin-tech-insights-react';
import { parseScorecardExemptions } from './exemptions';

export const useEntityChecks = (
  entityRef: CompoundEntityRef,
  checksId: string[],
): UseQueryResult<CheckResult[], Error> => {
  const api = useApi(techInsightsApiRef);
  const sortedIds = [...checksId].sort();
  const idsCsv = sortedIds.join(',');
  const refKey = stringifyEntityRef(entityRef);

  return useQuery({
    queryKey: ['scorecards', 'entityChecks', refKey, idsCsv],
    queryFn: () =>
      sortedIds.length === 0
        ? Promise.resolve([] as CheckResult[])
        : api.runChecks(entityRef, sortedIds),
  });
};

export type PortfolioRow = {
  entityRef: CompoundEntityRef;
  owner?: string;
  teams: string[];
  system?: string;
  results: CheckResult[];
  passed: number;
  failed: number;
};

const entityLabel = (entity: Entity): string => {
  const profileName = (entity.spec as any)?.profile?.displayName as
    | string
    | undefined;
  return profileName ?? entity.metadata.title ?? entity.metadata.name;
};

const relationsOfType = (entity: Entity, type: string): string[] => {
  const rels = entity.relations ?? [];
  return rels.filter(r => r.type === type).map(r => r.targetRef);
};

const systemOf = (entity: Entity): string | undefined => {
  return (entity.spec as any)?.system as string | undefined;
};

export const usePortfolioScorecards = (): UseQueryResult<
  PortfolioRow[],
  Error
> => {
  const techInsights = useApi(techInsightsApiRef);
  const catalog = useApi(catalogApiRef);

  return useQuery({
    queryKey: ['scorecards', 'portfolio'],
    queryFn: async () => {
      const [componentsResp, peopleResp] = await Promise.all([
        catalog.getEntities({ filter: { kind: 'Component' } }),
        catalog.getEntities({ filter: { kind: ['User', 'Group'] } }),
      ]);

      const labelByRef = new Map<string, string>();
      const userTeamsByRef = new Map<string, string[]>();
      for (const e of peopleResp.items) {
        const ref = stringifyEntityRef(e);
        labelByRef.set(ref, entityLabel(e));
        if (e.kind === 'User') {
          userTeamsByRef.set(ref, relationsOfType(e, 'memberOf'));
        }
      }

      const refs = componentsResp.items.map(getCompoundEntityRef);
      const bulk = await techInsights.runBulkChecks(refs, undefined);

      const metaByRef = new Map<
        string,
        {
          exempt: Set<string>;
          owner?: string;
          teams: string[];
          system?: string;
        }
      >();
      for (const entity of componentsResp.items) {
        const ownerRefs = relationsOfType(entity, 'ownedBy');
        const ownerRef = ownerRefs[0];
        const ownerLabel = ownerRef
          ? labelByRef.get(ownerRef) ?? ownerRef
          : undefined;
        const teamRefs = ownerRefs.flatMap(ref => {
          if (ref.startsWith('group:')) return [ref];
          return userTeamsByRef.get(ref) ?? [];
        });
        const teams = Array.from(
          new Set(teamRefs.map(ref => labelByRef.get(ref) ?? ref)),
        );
        metaByRef.set(stringifyEntityRef(entity), {
          exempt: parseScorecardExemptions(entity),
          owner: ownerLabel,
          teams,
          system: systemOf(entity),
        });
      }

      return bulk.map(row => {
        const meta = metaByRef.get(row.entity);
        const exempt = meta?.exempt ?? new Set<string>();
        const filtered = row.results.filter(r => !exempt.has(r.check.id));
        const passed = filtered.filter(r => r.result === true).length;
        return {
          entityRef: parseEntityRef(row.entity),
          owner: meta?.owner,
          teams: meta?.teams ?? [],
          system: meta?.system,
          results: filtered,
          passed,
          failed: filtered.length - passed,
        };
      });
    },
  });
};
